/**
 * Retrospective swing pivots on daily closes. Both fitted and calibrated raw
 * pivots need four subsequent daily bars before they can be shown.
 */
export function enforceAlternatingSwings (candidates, minReversal = 0.01, minTradingBars = 4) {
  const swings = []
  for (const point of candidates) {
    const last = swings[swings.length - 1]
    if (!last) {
      swings.push(point)
      continue
    }
    if (last.type === point.type) {
      const moreExtreme = point.type === 'peak'
        ? point.price > last.price
        : point.price < last.price
      if (moreExtreme) swings[swings.length - 1] = point
      continue
    }
    if (point.index - last.index >= minTradingBars &&
        Math.abs(point.price - last.price) / last.price >= minReversal) {
      swings.push(point)
    }
  }
  return swings
}

export function findSwingExtrema (list, startDate, endDate, minReversal = 0.01, minTradingBars = 4) {
  const stage = list.filter(item => item.trade_date >= startDate && item.trade_date <= endDate)
  if (stage.length < 9) return { series: Array(list.length).fill(null), points: [] }

  const prices = stage.map(item => Number(item.close))
  const fitted = Array(stage.length).fill(null)
  const weights = [-2, 3, 6, 7, 6, 3, -2]
  for (let i = 3; i < stage.length - 3; i++) {
    const window = prices.slice(i - 3, i + 4)
    if (window.some(price => !Number.isFinite(price) || price <= 0)) continue
    fitted[i] = window.reduce((sum, price, j) => sum + price * weights[j], 0) / 21
  }

  const candidates = []
  for (let i = 4; i < stage.length - 4; i++) {
    const before = fitted[i - 1]
    const value = fitted[i]
    const after = fitted[i + 1]
    if (before == null || value == null || after == null) continue
    if (before < value && value > after) candidates.push({ index: i, type: 'peak', price: value })
    if (before > value && value < after) candidates.push({ index: i, type: 'valley', price: value })
  }

  // Keep local candidates for calibration; duration applies to displayed raw pivots.
  const fittedSwings = enforceAlternatingSwings(candidates, minReversal, 1)
  const calibrated = fittedSwings.map((point, position) => {
    const previous = fittedSwings[position - 1]?.index
    const next = fittedSwings[position + 1]?.index
    const left = Math.max(0, point.index - 3,
      previous == null ? 0 : Math.floor((previous + point.index) / 2) + 1)
    const right = Math.min(stage.length - 1, point.index + 3,
      next == null ? stage.length - 1 : Math.floor((point.index + next) / 2))
    let best = point.index
    for (let i = left; i <= right; i++) {
      if (!Number.isFinite(prices[i]) || prices[i] <= 0) continue
      const better = point.type === 'peak' ? prices[i] > prices[best] : prices[i] < prices[best]
      if (better) best = i
    }
    const confirmationIndex = Math.max(point.index, best) + 4
    if (confirmationIndex >= stage.length) return null
    return {
      index: best,
      type: point.type,
      price: prices[best],
      date: stage[best].trade_date,
      confirmedDate: stage[confirmationIndex].trade_date,
      fittedDate: stage[point.index].trade_date,
      fittedPrice: point.price
    }
  }).filter(Boolean)

  // Raw-price calibration can shrink a fitted swing; enforce the same rule on
  // the displayed K-line points so the chart never shows adjacent equal types.
  const points = enforceAlternatingSwings(calibrated, minReversal, minTradingBars)
  const byDate = new Map(stage.map((item, index) => [item.trade_date, fitted[index]]))
  return { series: list.map(item => byDate.get(item.trade_date) ?? null), points }
}

export function hasDecliningPeaksAndValleys (points) {
  const recent = points.slice(-4)
  if (recent.length < 4) return false
  const peaks = recent.filter(point => point.type === 'peak').map(point => point.price)
  const valleys = recent.filter(point => point.type === 'valley').map(point => point.price)
  return peaks.length === 2 && valleys.length === 2 &&
    peaks[1] < peaks[0] && valleys[1] < valleys[0]
}
